import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useShop } from "@/contexts/ShopContext";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  "Pending",
  "Accepted",
  "Preparing",
  "Ready",
  "Out for Delivery",
  "Delivered",
  "Rejected",
  "Cancelled",
];

const TYPE_OPTIONS = [
  "Online",
  "Takeaway",
  "LiveCounter",
];

export default function OrderStatus() {
  const { currentShopId } = useShop();

  // =========================================================
  // STATE
  // =========================================================

  const [orders, setOrders] = useState([]);
  const [menuItems, setMenuItems] = useState([]);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const [loading, setLoading] = useState(false);

  // Edit modal
  const [editTarget, setEditTarget] = useState(null);
  const [editLines, setEditLines] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);
  const [loadingMenu, setLoadingMenu] = useState(false);

  // =========================================================
  // GET ORDER ID
  // =========================================================

  const getOrderId = (order) => {
    return (
      order?.id ||
      order?._id ||
      order?.order_id ||
      order?.orderId ||
      null
    );
  };

  // =========================================================
  // LOAD ORDERS
  // =========================================================

  const loadOrders = useCallback(async () => {
    if (!currentShopId) {
      setOrders([]);
      return;
    }

    try {
      setLoading(true);

      const response = await api.get("/orders", {
        params: {
          shop_id: currentShopId,
        },
      });

      const result = response.data;

      let orderList = [];

      if (Array.isArray(result)) {
        orderList = result;
      } else if (Array.isArray(result?.orders)) {
        orderList = result.orders;
      } else if (Array.isArray(result?.data)) {
        orderList = result.data;
      }

      setOrders(orderList);
    } catch (error) {
      console.error("Failed to load orders:", error);

      setOrders([]);

      toast.error(
        error.response?.data?.detail ||
          "Failed to load orders"
      );
    } finally {
      setLoading(false);
    }
  }, [currentShopId]);

  // =========================================================
  // LOAD MENU ITEMS
  // =========================================================

  const loadMenuItems = useCallback(async () => {
    if (!currentShopId) {
      setMenuItems([]);
      return;
    }

    try {
      setLoadingMenu(true);

      const response = await api.get("/menu-items", {
        params: {
          shop_id: currentShopId,
        },
      });

      const items = Array.isArray(response.data)
        ? response.data
        : response.data?.items || [];

      setMenuItems(items);

      console.log("Menu items:", items);
    } catch (error) {
      console.error(
        "Failed to load menu items:",
        error.response?.data || error
      );

      setMenuItems([]);
    } finally {
      setLoadingMenu(false);
    }
  }, [currentShopId]);

  // =========================================================
  // INITIAL LOAD
  // =========================================================

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    loadMenuItems();
  }, [loadMenuItems]);

  // =========================================================
  // ORDER HELPERS
  // =========================================================

  const getOrderTotal = (order) => {
    if (
      order?.total !== undefined &&
      order?.total !== null
    ) {
      return Number(order.total);
    }

    if (
      order?.total_amount !== undefined &&
      order?.total_amount !== null
    ) {
      return Number(order.total_amount);
    }

    if (
      order?.amount !== undefined &&
      order?.amount !== null
    ) {
      return Number(order.amount);
    }

    if (
      order?.grand_total !== undefined &&
      order?.grand_total !== null
    ) {
      return Number(order.grand_total);
    }

    if (
      order?.order_total !== undefined &&
      order?.order_total !== null
    ) {
      return Number(order.order_total);
    }

    return 0;
  };

  const getOrderStatus = (order) => {
    return String(
      order?.status ||
        order?.order_status ||
        ""
    ).trim();
  };

  const getCustomerName = (order) => {
    return (
      order?.customer_name ||
      order?.customer ||
      order?.customerName ||
      "Unknown"
    );
  };

  const getOrderType = (order) => {
    return String(
      order?.order_type ||
        order?.type ||
        order?.orderType ||
        ""
    );
  };

  const getOrderNumber = (order, index) => {
    return (
      order?.order_number ||
      order?.order_no ||
      order?.orderNo ||
      order?.order_id ||
      order?.id ||
      order?._id ||
      `#${index + 1}`
    );
  };

  const isCancelledOrRejected = (order) => {
    const status =
      getOrderStatus(order).toLowerCase();

    return (
      status === "cancelled" ||
      status === "canceled" ||
      status === "rejected"
    );
  };

  const getPaymentStatus = (order) => {
    if (isCancelledOrRejected(order)) {
      return "—";
    }

    return (
      order?.payment_status ||
      order?.paymentStatus ||
      order?.payment ||
      "Pending"
    );
  };

  const formatAmount = (amount) => {
    const value = Number(amount);

    if (Number.isNaN(value)) {
      return "—";
    }

    return `₹${value.toFixed(0)}`;
  };

  // =========================================================
  // FILTER
  // =========================================================

  const filteredOrders = orders.filter(
    (order) => {
      const customer =
        getCustomerName(order).toLowerCase();

      const status =
        getOrderStatus(order);

      const type =
        getOrderType(order);

      const matchesSearch =
        !search ||
        customer.includes(
          search.toLowerCase()
        );

      const matchesStatus =
        !statusFilter ||
        status.toLowerCase() ===
          statusFilter.toLowerCase();

      const matchesType =
        !typeFilter ||
        type.toLowerCase() ===
          typeFilter.toLowerCase();

      return (
        matchesSearch &&
        matchesStatus &&
        matchesType
      );
    }
  );

  // =========================================================
  // STATUS STYLE
  // =========================================================

  const getStatusClass = (status) => {
    const value =
      String(status).toLowerCase();

    if (
      value.includes("cancel") ||
      value.includes("reject")
    ) {
      return "status-danger";
    }

    if (
      value.includes("deliver") ||
      value.includes("ready")
    ) {
      return "status-success";
    }

    if (
      value.includes("pending")
    ) {
      return "status-warning";
    }

    return "status-info";
  };

  // =========================================================
  // CAN EDIT ORDER
  // =========================================================

  const canEditOrder = (order) => {
    const status =
      getOrderStatus(order)
        .toLowerCase();

    return ![
      "cancelled",
      "canceled",
      "rejected",
      "delivered",
    ].includes(status);
  };

  // =========================================================
  // OPEN EDIT
  // =========================================================

  const openEdit = async (order) => {
    const lines = Array.isArray(
      order?.lines
    )
      ? order.lines
      : [];

    const normalizedLines =
      lines.map((line) => ({
        item_id: String(
          line.item_id
        ),
        name:
          line.name ||
          "Unknown Item",
        price:
          Number(line.price) || 0,
        qty:
          Number(line.qty) || 1,
        prepared_qty:
          Number(
            line.prepared_qty || 0
          ),
        status:
          line.status ||
          "pending",
      }));

    setEditTarget(order);
    setEditLines(
      normalizedLines
    );

    await loadMenuItems();
  };

  // =========================================================
  // CLOSE EDIT
  // =========================================================

  const closeEdit = () => {
    if (savingEdit) {
      return;
    }

    setEditTarget(null);
    setEditLines([]);
  };

  // =========================================================
  // INCREASE QUANTITY
  // =========================================================

  const increaseQuantity = (
    itemId
  ) => {
    setEditLines(
      (currentLines) =>
        currentLines.map(
          (line) => {
            if (
              String(
                line.item_id
              ) !==
              String(itemId)
            ) {
              return line;
            }

            return {
              ...line,
              qty:
                Number(
                  line.qty
                ) + 1,
            };
          }
        )
    );
  };

  // =========================================================
  // DECREASE QUANTITY
  // =========================================================

  const decreaseQuantity = (
    itemId
  ) => {
    setEditLines(
      (currentLines) =>
        currentLines
          .map((line) => {
            if (
              String(
                line.item_id
              ) !==
              String(itemId)
            ) {
              return line;
            }

            const currentQty =
              Number(
                line.qty
              );

            const preparedQty =
              Number(
                line.prepared_qty ||
                  0
              );

            const minimumQty =
              Math.max(
                1,
                preparedQty
              );

            const newQty =
              currentQty - 1;

            if (
              newQty <
              minimumQty
            ) {
              return line;
            }

            return {
              ...line,
              qty: newQty,
            };
          })
          .filter(
            (line) =>
              Number(
                line.qty
              ) > 0
          )
    );
  };

  // =========================================================
  // DIRECT QUANTITY INPUT
  // =========================================================

  const updateQuantity = (
    itemId,
    value
  ) => {
    let newQty = parseInt(
      value,
      10
    );

    if (
      Number.isNaN(newQty) ||
      newQty < 1
    ) {
      newQty = 1;
    }

    setEditLines(
      (currentLines) =>
        currentLines.map(
          (line) => {
            if (
              String(
                line.item_id
              ) !==
              String(itemId)
            ) {
              return line;
            }

            const preparedQty =
              Number(
                line.prepared_qty ||
                  0
              );

            newQty = Math.max(
              newQty,
              preparedQty
            );

            return {
              ...line,
              qty: newQty,
            };
          }
        )
    );
  };

  // =========================================================
  // ADD NEW ITEM
  // =========================================================

  const addNewItem = (
    itemId
  ) => {
    if (!itemId) {
      return;
    }

    const selectedItem =
      menuItems.find(
        (item) =>
          String(
            item.id ||
              item._id
          ) ===
          String(itemId)
      );

    if (!selectedItem) {
      toast.error(
        "Menu item not found"
      );
      return;
    }

    const selectedItemId =
      String(
        selectedItem.id ||
          selectedItem._id
      );

    const existingLine =
      editLines.find(
        (line) =>
          String(
            line.item_id
          ) ===
          selectedItemId
      );

    if (existingLine) {
      increaseQuantity(
        selectedItemId
      );
      return;
    }

    setEditLines(
      (currentLines) => [
        ...currentLines,
        {
          item_id:
            selectedItemId,
          name:
            selectedItem.name ||
            "Unknown Item",
          price:
            Number(
              selectedItem.price ||
                0
            ),
          qty: 1,
          prepared_qty: 0,
          status: "pending",
        },
      ]
    );
  };

  // =========================================================
  // REMOVE ITEM
  // =========================================================

  const removeItem = (
    itemId
  ) => {
    const line =
      editLines.find(
        (item) =>
          String(
            item.item_id
          ) ===
          String(itemId)
      );

    if (!line) {
      return;
    }

    const preparedQty =
      Number(
        line.prepared_qty || 0
      );

    if (preparedQty > 0) {
      toast.error(
        `${line.name} has already been prepared and cannot be removed.`
      );

      return;
    }

    setEditLines(
      (currentLines) =>
        currentLines.filter(
          (item) =>
            String(
              item.item_id
            ) !==
            String(itemId)
        )
    );
  };

  // =========================================================
  // EDIT TOTAL
  // =========================================================

  const editTotal =
    editLines.reduce(
      (total, line) => {
        const price =
          Number(
            line.price
          ) || 0;

        const qty =
          Number(
            line.qty
          ) || 0;

        return (
          total +
          price * qty
        );
      },
      0
    );

  // =========================================================
  // TOTAL QUANTITY
  // =========================================================

  const editQuantity =
    editLines.reduce(
      (total, line) => {
        return (
          total +
          (Number(
            line.qty
          ) || 0)
        );
      },
      0
    );

  // =========================================================
  // SAVE EDIT
  // =========================================================

  const saveEdit = async () => {
    if (!editTarget) {
      return;
    }

    if (
      editLines.length === 0
    ) {
      toast.error(
        "Order must contain at least one item."
      );
      return;
    }

    const invalidLine =
      editLines.find(
        (line) =>
          !line.item_id ||
          Number(line.qty) <= 0
      );

    if (invalidLine) {
      toast.error(
        "Please check all item quantities."
      );
      return;
    }

    const orderId =
      getOrderId(editTarget);

    if (!orderId) {
      toast.error(
        "Order ID was not found."
      );
      return;
    }

    try {
      setSavingEdit(true);

      const response =
        await api.patch(
          `/orders/${orderId}`,
          {
            lines:
              editLines.map(
                (line) => ({
                  item_id:
                    String(
                      line.item_id
                    ),
                  qty: Number(
                    line.qty
                  ),
                })
              ),
          }
        );

      const updatedOrder =
        response.data;

      setEditTarget(null);
      setEditLines([]);

      await loadOrders();

      const updatedTotal =
        Number(
          updatedOrder?.total
        );

      if (
        !Number.isNaN(
          updatedTotal
        )
      ) {
        toast.success(
          `Order updated successfully. New total: ₹${updatedTotal.toFixed(
            0
          )}`
        );
      } else {
        toast.success(
          "Order updated successfully."
        );
      }
    } catch (error) {
      console.error(
        "Failed to update order:",
        error
      );

      toast.error(
        error.response?.data?.detail ||
          "Failed to update order"
      );
    } finally {
      setSavingEdit(false);
    }
  };

  // =========================================================
  // CANCEL ORDER
  // =========================================================

  const cancelOrder = async () => {
    if (!editTarget) {
      return;
    }

    const orderId =
      getOrderId(editTarget);

    if (!orderId) {
      toast.error(
        "Order ID was not found."
      );
      return;
    }

    const orderNumber =
      getOrderNumber(
        editTarget,
        0
      );

    // First confirmation
    const confirmed =
      window.confirm(
        `Are you sure you want to cancel Order #${orderNumber}?`
      );

    if (!confirmed) {
      return;
    }

    // Cancellation reason
    const reason =
      window.prompt(
        "Please enter the cancellation reason:",
        "Cancelled by customer"
      );

    // User pressed Cancel on the prompt
    if (reason === null) {
      return;
    }

    const finalReason =
      reason.trim() ||
      "Cancelled by user";

    try {
      setSavingEdit(true);

      await api.post(
        `/orders/${orderId}/cancel`,
        {
          reason: finalReason,
        }
      );

      toast.success(
        `Order #${orderNumber} cancelled successfully`
      );

      // Close modal
      setEditTarget(null);
      setEditLines([]);

      // Refresh order table
      await loadOrders();

    } catch (error) {
      console.error(
        "Failed to cancel order:",
        error
      );

      toast.error(
        error.response?.data?.detail ||
          "Failed to cancel order"
      );
    } finally {
      setSavingEdit(false);
    }
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <section
      className="view"
      data-testid="order-status-page"
    >

      {/* =====================================================
          PAGE HEADER
      ===================================================== */}

      <div className="page-head">
        <div>

          <div className="eyebrow">
            EVERY TICKET · EVERY STAGE
          </div>

          <h1>
            Order Status
          </h1>

          <p>
            Newest orders first.
            Edit quantities, add
            items and update the
            total.
          </p>

        </div>
      </div>

      {/* =====================================================
          FILTER CARD
      ===================================================== */}

      <div className="card">

        <div className="toolbar">

          <input
            className="input"
            placeholder="⌕  Search customer"
            value={search}
            onChange={(e) =>
              setSearch(
                e.target.value
              )
            }
          />

          <select
            className="select"
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(
                e.target.value
              )
            }
          >

            <option value="">
              All statuses
            </option>

            {STATUS_OPTIONS.map(
              (status) => (
                <option
                  key={status}
                  value={status}
                >
                  {status}
                </option>
              )
            )}

          </select>

          <select
            className="select"
            value={typeFilter}
            onChange={(e) =>
              setTypeFilter(
                e.target.value
              )
            }
          >

            <option value="">
              All types
            </option>

            {TYPE_OPTIONS.map(
              (type) => (
                <option
                  key={type}
                  value={type}
                >
                  {type}
                </option>
              )
            )}

          </select>

        </div>

        {/* ===================================================
            TABLE
        =================================================== */}

        <div
          style={{
            overflowX: "auto",
          }}
        >

          <table>

            <thead>
              <tr>

                <th>
                  ORDER NO#
                </th>

                <th>
                  CUSTOMER
                </th>

                <th>
                  TYPE
                </th>

                <th>
                  STATUS
                </th>

                <th>
                  PAYMENT
                </th>

                <th>
                  TOTAL
                </th>

                <th
                  style={{
                    textAlign:
                      "center",
                    minWidth:
                      "100px",
                  }}
                >
                  ACTION
                </th>

              </tr>
            </thead>

            <tbody>

              {/* LOADING */}

              {loading && (
                <tr>
                  <td
                    colSpan="7"
                    style={{
                      textAlign:
                        "center",
                      padding:
                        "30px",
                    }}
                  >
                    Loading orders...
                  </td>
                </tr>
              )}

              {/* ORDERS */}

              {!loading &&
                filteredOrders.map(
                  (
                    order,
                    index
                  ) => {

                    const status =
                      getOrderStatus(
                        order
                      );

                    const cancelledOrRejected =
                      isCancelledOrRejected(
                        order
                      );

                    const total =
                      getOrderTotal(
                        order
                      );

                    const payment =
                      getPaymentStatus(
                        order
                      );

                    const editable =
                      canEditOrder(
                        order
                      );

                    return (
                      <tr
                        key={
                          getOrderId(
                            order
                          ) ||
                          index
                        }
                      >

                        {/* ORDER NUMBER */}

                        <td>
                          <span className="mono">
                            #
                            {getOrderNumber(
                              order,
                              index
                            )}
                          </span>
                        </td>

                        {/* CUSTOMER */}

                        <td>
                          {getCustomerName(
                            order
                          )}
                        </td>

                        {/* TYPE */}

                        <td>
                          {getOrderType(
                            order
                          )}
                        </td>

                        {/* STATUS */}

                        <td>

                          <span
                            className={`status ${getStatusClass(
                              status
                            )}`}
                          >
                            {status ||
                              "Pending"}
                          </span>

                        </td>

                        {/* PAYMENT */}

                        <td>

                          {cancelledOrRejected ? (

                            <span className="mono">
                              —
                            </span>

                          ) : (

                            <span
                              className={`status ${
                                String(
                                  payment
                                ).toLowerCase() ===
                                "paid"
                                  ? "status-success"
                                  : "status-warning"
                              }`}
                            >
                              {payment}
                            </span>

                          )}

                        </td>

                        {/* TOTAL */}

                        <td>

                          {cancelledOrRejected ? (

                            <span className="mono">
                              —
                            </span>

                          ) : (

                            <span className="mono">
                              {formatAmount(
                                total
                              )}
                            </span>

                          )}

                        </td>
{/* ACTION */}

                        <td
                          style={{
                            textAlign:
                              "center",
                          }}
                        >

                          {editable ? (

                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() =>
                                openEdit(
                                  order
                                )
                              }
                              style={{
                                padding:
                                  "7px 14px",
                                fontSize:
                                  "12px",
                                fontWeight:
                                  600,
                                whiteSpace:
                                  "nowrap",
                                cursor:
                                  "pointer",
                              }}
                            >
                              ✎ Edit
                            </button>

                          ) : (

                            <span
                              style={{
                                color:
                                  "var(--fg-muted)",
                                fontSize:
                                  "12px",
                              }}
                            >
                              —
                            </span>

                          )}

                        </td>

                      </tr>
                    );
                  }
                )}

              {/* NO ORDERS */}

              {!loading &&
                filteredOrders.length ===
                  0 && (
                  <tr>
                    <td
                      colSpan="7"
                      style={{
                        textAlign:
                          "center",
                        padding:
                          "40px",
                      }}
                    >
                      No orders found
                    </td>
                  </tr>
                )}

            </tbody>

          </table>

        </div>

        {/* FOOTER NOTE */}

        <div className="note">
          Rejected and cancelled
          orders do not display
          payment status or total
          amount.
        </div>

      </div>

      {/* =====================================================
          EDIT ORDER MODAL
      ===================================================== */}

      {editTarget && (
        <div
          style={{
            position:
              "fixed",
            inset: 0,
            background:
              "rgba(0, 0, 0, 0.50)",
            display:
              "flex",
            alignItems:
              "center",
            justifyContent:
              "center",
            zIndex: 9999,
            padding:
              "20px",
          }}
        >

          <div
            style={{
              width:
                "100%",
              maxWidth:
                "780px",
              maxHeight:
                "90vh",
              overflowY:
                "auto",
              background:
                "var(--surface, #ffffff)",
              borderRadius:
                "16px",
              boxShadow:
                "0 25px 70px rgba(0,0,0,.30)",
              padding:
                "24px",
            }}
          >

            {/* =============================================
                MODAL HEADER
            ============================================= */}

            <div
              style={{
                display:
                  "flex",
                alignItems:
                  "center",
                justifyContent:
                  "space-between",
                marginBottom:
                  "20px",
              }}
            >

              <div>

                <div className="eyebrow">
                  EDIT ORDER
                </div>

                <h2
                  style={{
                    margin:
                      "4px 0",
                  }}
                >
                  Order #
                  {getOrderNumber(
                    editTarget,
                    0
                  )}
                </h2>

                <p
                  style={{
                    margin:
                      0,
                    color:
                      "var(--fg-muted)",
                    fontSize:
                      "13px",
                  }}
                >
                  Customer:{" "}
                  {getCustomerName(
                    editTarget
                  )}
                </p>

              </div>

              <button
                type="button"
                onClick={
                  closeEdit
                }
                disabled={
                  savingEdit
                }
                style={{
                  width:
                    "38px",
                  height:
                    "38px",
                  border:
                    "1px solid var(--border, #ddd)",
                  borderRadius:
                    "8px",
                  background:
                    "transparent",
                  fontSize:
                    "22px",
                  cursor:
                    "pointer",
                }}
              >
                ×
              </button>

            </div>

            {/* =============================================
                ADD NEW ITEM
            ============================================= */}

            <div
              style={{
                border:
                  "1px solid var(--border, #ddd)",
                borderRadius:
                  "12px",
                padding:
                  "16px",
                marginBottom:
                  "20px",
              }}
            >

              <label
                style={{
                  display:
                    "block",
                  fontSize:
                    "13px",
                  fontWeight:
                    700,
                  marginBottom:
                    "8px",
                }}
              >
                Add New Item
              </label>

              <select
                className="select"
                value=""
                disabled={
                  savingEdit ||
                  loadingMenu
                }
                onChange={(e) =>
                  addNewItem(
                    e.target.value
                  )
                }
                style={{
                  width:
                    "100%",
                  minHeight:
                    "42px",
                }}
              >

                <option value="">
                  {loadingMenu
                    ? "Loading menu items..."
                    : "+ Select item to add"}
                </option>

                {menuItems.map(
                  (item) => {

                    const itemId =
                      item.id ||
                      item._id;

                    const stock =
                      item.stock;

                    return (
                      <option
                        key={
                          itemId
                        }
                        value={
                          itemId
                        }
                      >
                        {item.name} — ₹
                        {Number(
                          item.price ||
                            0
                        ).toFixed(
                          0
                        )}

                        {stock !==
                          null &&
                        stock !==
                          undefined
                          ? ` — Stock: ${stock}`
                          : ""}
                      </option>
                    );
                  }
                )}

              </select>

            </div>

            {/* =============================================
                ORDER ITEMS
            ============================================= */}

            <div>

              <div
                style={{
                  display:
                    "flex",
                  alignItems:
                    "center",
                  justifyContent:
                    "space-between",
                  marginBottom:
                    "10px",
                }}
              >

                <strong
                  style={{
                    fontSize:
                      "15px",
                  }}
                >
                  Order Items
                </strong>

                <span
                  style={{
                    fontSize:
                      "12px",
                    color:
                      "var(--fg-muted)",
                  }}
                >
                  {editQuantity}{" "}
                  total quantity
                </span>

              </div>

              {editLines.length ===
                0 && (
                <div
                  style={{
                    textAlign:
                      "center",
                    padding:
                      "30px",
                    color:
                      "var(--fg-muted)",
                    border:
                      "1px dashed var(--border, #ddd)",
                    borderRadius:
                      "10px",
                  }}
                >
                  No items in
                  this order.
                </div>
              )}

              {/* ITEM LIST */}

              {editLines.map(
                (line) => {

                  const lineTotal =
                    Number(
                      line.price
                    ) *
                    Number(
                      line.qty
                    );

                  const preparedQty =
                    Number(
                      line.prepared_qty ||
                        0
                    );

                  return (
                    <div
                      key={
                        line.item_id
                      }
                      style={{
                        borderBottom:
                          "1px solid var(--border, #ddd)",
                        padding:
                          "15px 0",
                      }}
                    >

                      <div
                        style={{
                          display:
                            "flex",
                          alignItems:
                            "center",
                          gap:
                            "12px",
                        }}
                      >

                        {/* ITEM NAME */}

                        <div
                          style={{
                            flex:
                              1,
                            minWidth:
                              "150px",
                          }}
                        >

                          <div
                            style={{
                              fontWeight:
                                700,
                              fontSize:
                                "14px",
                            }}
                          >
                            {
                              line.name
                            }
                          </div>

                          <div
                            style={{
                              fontSize:
                                "12px",
                              color:
                                "var(--fg-muted)",
                              marginTop:
                                "4px",
                            }}
                          >
                            ₹
                            {Number(
                              line.price
                            ).toFixed(
                              0
                            )}{" "}
                            each
                          </div>

                          {preparedQty >
                            0 && (
                            <div
                              style={{
                                fontSize:
                                  "11px",
                                marginTop:
                                  "4px",
                                color:
                                  "var(--fg-muted)",
                              }}
                            >
                              Prepared:{" "}
                              {
                                preparedQty
                              }
                            </div>
                          )}

                        </div>

                        {/* QUANTITY */}

                        <div
                          style={{
                            display:
                              "flex",
                            alignItems:
                              "center",
                            gap:
                              "5px",
                          }}
                        >

                          <button
                            type="button"
                            onClick={() =>
                              decreaseQuantity(
                                line.item_id
                              )
                            }
                            disabled={
                              savingEdit ||
                              Number(
                                line.qty
                              ) <=
                                Math.max(
                                  1,
                                  preparedQty
                                )
                            }
                            style={{
                              width:
                                "38px",
                              height:
                                "38px",
                              border:
                                "1px solid var(--border, #ddd)",
                              borderRadius:
                                "8px",
                              background:
                                "var(--surface, #fff)",
                              cursor:
                                "pointer",
                              fontSize:
                                "20px",
                              fontWeight:
                                700,
                            }}
                          >
                            −
                          </button>

                          <input
                            type="number"
                            min={Math.max(
                              1,
                              preparedQty
                            )}
                            value={
                              line.qty
                            }
                            disabled={
                              savingEdit
                            }
                            onChange={(
                              e
                            ) =>
                              updateQuantity(
                                line.item_id,
                                e.target
                                  .value
                              )
                            }
                            style={{
                              width:
                                "62px",
                              height:
                                "38px",
                              border:
                                "1px solid var(--border, #ddd)",
                              borderRadius:
                                "8px",
                              textAlign:
                                "center",
                              fontWeight:
                                700,
                              fontSize:
                                "14px",
                            }}
                          />

                          <button
                            type="button"
                            onClick={() =>
                              increaseQuantity(
                                line.item_id
                              )
                            }
                            disabled={
                              savingEdit
                            }
                            style={{
                              width:
                                "38px",
                              height:
                                "38px",
                              border:
                                "1px solid var(--border, #ddd)",
                              borderRadius:
                                "8px",
                              background:
                                "var(--surface, #fff)",
                              cursor:
                                "pointer",
                              fontSize:
                                "20px",
                              fontWeight:
                                700,
                            }}
                          >
                            +
                          </button>

                        </div>

                        {/* LINE TOTAL */}

                        <div
                          style={{
                            width:
                              "100px",
                            textAlign:
                              "right",
                            fontWeight:
                              700,
                            fontFamily:
                              "monospace",
                          }}
                        >
                          ₹
                          {lineTotal.toFixed(
                            0
                          )}
                        </div>

                        {/* REMOVE */}

                        <button
                          type="button"
                          onClick={() =>
                            removeItem(
                              line.item_id
                            )
                          }
                          disabled={
                            savingEdit ||
                            preparedQty >
                              0
                          }
                          title={
                            preparedQty >
                            0
                              ? "Item already prepared"
                              : "Remove item"
                          }
                          style={{
                            width:
                              "38px",
                            height:
                              "38px",
                            border:
                              "1px solid #e0b0b0",
                            borderRadius:
                              "8px",
                            background:
                              "#fff5f5",
                            color:
                              "#b42318",
                            cursor:
                              "pointer",
                            fontSize:
                              "18px",
                          }}
                        >
                          ×
                        </button>

                      </div>

                    </div>
                  );
                }
              )}

            </div>

            {/* =============================================
                UPDATED TOTAL
            ============================================= */}

            <div
              style={{
                marginTop:
                  "20px",
                padding:
                  "18px 20px",
                borderRadius:
                  "12px",
                background:
                  "var(--surface-2, #f7f7f4)",
                border:
                  "1px solid var(--border, #ddd)",
                display:
                  "flex",
                alignItems:
                  "center",
                justifyContent:
                  "space-between",
              }}
            >

              <div>

                <div
                  style={{
                    fontSize:
                      "11px",
                    textTransform:
                      "uppercase",
                    letterSpacing:
                      "0.08em",
                    color:
                      "var(--fg-muted)",
                    fontWeight:
                      700,
                  }}
                >
                  Updated Total
                </div>

                <div
                  style={{
                    marginTop:
                      "5px",
                    fontSize:
                      "12px",
                    color:
                      "var(--fg-muted)",
                  }}
                >
                  {editLines.length}{" "}
                  item
                  {editLines.length !==
                  1
                    ? "s"
                    : ""}{" "}
                  ·{" "}
                  {editQuantity}{" "}
                  quantity
                </div>

              </div>

              <div
                style={{
                  fontSize:
                    "28px",
                  fontWeight:
                    800,
                  fontFamily:
                    "monospace",
                }}
              >
                ₹
                {editTotal.toFixed(
                  0
                )}
              </div>

            </div>

            {/* =============================================
                MODAL FOOTER
            ============================================= */}

            <div
              style={{
                display:
                  "flex",
                justifyContent:
                  "space-between",
                alignItems:
                  "center",
                gap:
                  "10px",
                marginTop:
                  "20px",
              }}
            >

              {/* CANCEL ORDER */}

              <button
                type="button"
                onClick={
                  cancelOrder
                }
                disabled={
                  savingEdit
                }
                style={{
                  minWidth:
                    "130px",
                  padding:
                    "10px 16px",
                  border:
                    "1px solid #dc2626",
                  borderRadius:
                    "8px",
                  background:
                    "#fff5f5",
                  color:
                    "#dc2626",
                  fontWeight:
                    700,
                  cursor:
                    savingEdit
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                Cancel Order
              </button>

              {/* RIGHT SIDE */}

              <div
                style={{
                  display:
                    "flex",
                  gap:
                    "10px",
                }}
              >

                {/* CLOSE */}

                <button
                  type="button"
                  onClick={
                    closeEdit
                  }
                  disabled={
                    savingEdit
                  }
                  className="btn btn-secondary"
                  style={{
                    minWidth:
                      "100px",
                  }}
                >
                  Close
                </button>

                {/* SAVE */}

                <button
                  type="button"
                  onClick={
                    saveEdit
                  }
                  disabled={
                    savingEdit ||
                    editLines.length ===
                      0
                  }
                  className="btn btn-primary"
                  style={{
                    minWidth:
                      "140px",
                  }}
                >
                  {savingEdit
                    ? "Saving..."
                    : "Save Changes"}
                </button>

              </div>

            </div>

          </div>

        </div>
      )}

    </section>
  );
}